import List from '@jetbrains/ring-ui/components/list/list'
import {memo, Suspense, useCallback, useMemo} from 'react'

import {openDialog} from '../../../../actions'
import {usePermission} from '../../../../hooks/usePermission'
import {getTestHistoryHref} from '../../../../routes'
import {getBuild, getBuildType} from '../../../../selectors'
import {restApi} from '../../../../services/rest'
import type {BuildId, TestOccurrenceId} from '../../../../types'
import {DialogType, InvestigationStates, Permission, stringifyId} from '../../../../types'
import {BS} from '../../../../types/BS_types'
import {emptyArray} from '../../../../utils/empty'
import ArrowPopupMenu from '../../../common/ArrowPopupMenu/ArrowPopupMenu'
import {getInvestigationInCurrentScope} from '../../../common/Investigations/Investigations.selectors'
import Link from '../../../common/Link/Link'
import RouterLink from '../../../common/RouterLink/RouterLink'
import RouterLinkWrapper from '../../../common/RouterLinkWrapper/RouterLinkWrapper'
import {getFullBuildLogLinkParams} from '../../BuildLog/BuildLog.utils'
import {assignTestInvestigations} from '../Tests.actions'
import {getTestOccurenceArg} from '../Tests.rest'
import {getFlakyTestData, getTestOccurrence} from '../Tests.selectors'
import {TestStatus} from '../Tests.types'

import styles from './BuildTestActions.css'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

type Props = {
  className?: string
  anchorClassName?: string
  buildId: BuildId | null | undefined
  testOccurrenceId?: TestOccurrenceId
}

function BuildTestActions({className, anchorClassName, buildId, testOccurrenceId}: Props) {
  const dispatch = useAppDispatch()

  const build = useAppSelector(state => getBuild(state, buildId))
  const buildType = useAppSelector(state => getBuildType(state, build?.buildType))
  const testOccurrence = useAppSelector(state => getTestOccurrence(state, testOccurrenceId))

  const {
    test,
    status,
    logAnchor,
    currentlyMuted,
    build: testBuild,
    currentlyInvestigated,
  } = testOccurrence ?? {}

  const testId = test?.id
  const {id: testBuildId, buildType: testBuildType} = testBuild ?? {}
  const testProject = testBuildType?.project
  const testProjectId = testProject?.id
  const investigations = test?.investigations?.investigation ?? emptyArray

  const investigationInCurrentScope = useAppSelector(state =>
    getInvestigationInCurrentScope(state, investigations, buildType?.projectId, buildType?.id),
  )
  const isFlakyTest =
    useAppSelector(state => getFlakyTestData(state, buildId, testId)?.isFlaky) ?? false

  const hasPermissionForInvestigation = usePermission(
    Permission.ASSIGN_INVESTIGATION,
    testProjectId,
  )
  const hasPermissionForManageProblems = usePermission(
    Permission.MANAGE_BUILD_PROBLEMS,
    testProjectId,
  )

  const [fetchTestOccurrence] =
    restApi.endpoints.getTestOccurrenceNormalized.useLazyQuerySubscription()
  const assignInvestigationHandler = useCallback(
    (presetFix = false) => {
      if (testId != null && testBuildId && testProjectId != null) {
        const submitHandler = () =>
          testOccurrenceId != null &&
          fetchTestOccurrence(
            getTestOccurenceArg(testOccurrenceId, {
              withNewFailure: true,
              withMetadataCount: true,
              withNextFixed: true,
              withFirstFailed: true,
              withMuteInfo: true,
              withInvestigationInfo: true,
            }),
          )
        dispatch(
          assignTestInvestigations(
            testProjectId,
            [testId],
            isFlakyTest ? [testId] : [],
            [testBuildId],
            presetFix,
            submitHandler,
          ),
        )
      }
    },
    [
      testId,
      testBuildId,
      testProjectId,
      dispatch,
      isFlakyTest,
      testOccurrenceId,
      fetchTestOccurrence,
    ],
  )

  const onAssignInvestigation = useCallback(
    () => assignInvestigationHandler(),
    [assignInvestigationHandler],
  )
  const onFixInvestigation = useCallback(
    () => assignInvestigationHandler(true),
    [assignInvestigationHandler],
  )
  const onShowInvestigationHistory = useCallback(
    () => dispatch(openDialog(stringifyId(testId), DialogType.INVESTIGATION_HISTORY)),
    [dispatch, testId],
  )
  const onOpenInIDE = useCallback(
    () =>
      logAnchor != null &&
      BS?.Activator?.doOpen(`test?buildId=${stringifyId(testBuildId)}&testId=${logAnchor}`),
    [logAnchor, testBuildId],
  )

  return useMemo(() => {
    const elements = []
    elements.push({
      key: 'testHistory',
      rgItemType: List.ListProps.Type.CUSTOM,
      className: styles.customLinkWrapper,
      template: (
        <RouterLinkWrapper>
          <Link
            href={getTestHistoryHref(testId, {currentProjectId: stringifyId(testProjectId)})}
            className={styles.customLink}
          >
            {'Show test history'}
          </Link>
        </RouterLinkWrapper>
      ),
    })

    elements.push({
      rgItemType: List.ListProps.Type.ITEM,
      title: '',
      label: 'Show investigation history',
      onClick: onShowInvestigationHistory,
    })

    if (hasPermissionForInvestigation) {
      elements.push({
        rgItemType: List.ListProps.Type.ITEM,
        title: '',
        label:
          currentlyInvestigated === true ||
          investigationInCurrentScope?.state === InvestigationStates.FIXED
            ? 'Reassign investigation...'
            : 'Assign investigation...',
        onClick: onAssignInvestigation,
      })

      if (
        currentlyInvestigated === true &&
        investigationInCurrentScope?.state !== InvestigationStates.FIXED
      ) {
        elements.push({
          rgItemType: List.ListProps.Type.ITEM,
          title: '',
          label: 'Fix investigation...',
          onClick: onFixInvestigation,
        })
      }
    }

    if (hasPermissionForManageProblems) {
      elements.push({
        rgItemType: List.ListProps.Type.ITEM,
        title: '',
        label: currentlyMuted === true ? 'Unmute test...' : 'Mute test...',
        onClick: onAssignInvestigation,
      })
    }

    if (status === TestStatus.FAILURE && logAnchor != null) {
      elements.push({
        rgItemType: List.ListProps.Type.ITEM,
        title: '',
        label: 'Open in IDE',
        onClick: onOpenInIDE,
      })
    }

    if (testBuildId != null && Number(logAnchor) != null) {
      elements.push({
        rgItemType: List.ListProps.Type.CUSTOM,
        className: styles.customLinkWrapper,
        template: (
          <RouterLink
            params={getFullBuildLogLinkParams({
              buildId: testBuildId,
              line: logAnchor,
              expandState: [Number(logAnchor)],
              logFilter: 'debug',
            })}
            className={styles.customLink}
          >
            {'Show in Build Log'}
          </RouterLink>
        ),
      })
    }

    return (
      <ArrowPopupMenu className={className} anchorClassName={anchorClassName} elements={elements} />
    )
  }, [
    testId,
    testProjectId,
    onShowInvestigationHistory,
    hasPermissionForInvestigation,
    hasPermissionForManageProblems,
    status,
    logAnchor,
    testBuildId,
    className,
    anchorClassName,
    currentlyInvestigated,
    investigationInCurrentScope?.state,
    onAssignInvestigation,
    onFixInvestigation,
    currentlyMuted,
    onOpenInIDE,
  ])
}

export default memo<Props>(function BuildTestActionsContainer(props) {
  return (
    <Suspense>
      <BuildTestActions {...props} />
    </Suspense>
  )
})
